import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

import { useClient } from 'urql';
import { pipe, subscribe } from 'wonka';

import { config } from '../../../config/config';
import { TransactionsDocument } from '../graphql/transactions.graphql';
import type {
    ITransactionsSubscription,
    ITransactionsSubscriptionVariables,
} from '../graphql/transactions.graphql';

export function useTransactionComplete(
    handler: (transaction: {
        amount: number;
        currencyCode: string;
        operationType: 'credit' | 'debit';
    }) => void,
) {
    const client = useClient();
    const tenantId = config.TENANT_ID;
    const auth = useAuth();
    const userProfileId = auth?.user?.profile.user_profile_id as string;

    useEffect(() => {
        const source = client.subscription<
            ITransactionsSubscription,
            ITransactionsSubscriptionVariables
        >(TransactionsDocument, {
            tenantId,
            userProfileId,
        });

        const subscription = pipe(
            source,
            subscribe(({ data }) => {
                const payload = data?.transactions?.payload;

                if (!payload) {
                    return;
                }

                try {
                    const {
                        Amount: amount,
                        CurrencyCode: currencyCode,
                        OperationType: operationType,
                    } = JSON.parse(payload) as {
                        Amount: number;
                        CurrencyCode: string;
                        OperationType: 'Credit' | 'Debit';
                    };

                    handler({
                        amount,
                        currencyCode,
                        operationType: operationType === 'Credit' ? 'credit' : 'debit',
                    });
                } catch (e) {
                    console.error(e);
                }
            }),
        );

        return () => {
            subscription.unsubscribe();
        };
    }, [client, handler, userProfileId, tenantId]);
}
